import { EmailAddress } from '@dreryk/email-address-common-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';

import { BindingCreator, FormContextValues, phoneNumberFormatter, TranslatableText } from '@dreryk/react-common';
import { AutoLayout, Text, TextSeparator, TextSeparatorType } from '@dreryk/react-components';

import { EmailAddressInput } from './EmailAddressInput';
import { HealthcareBasicData } from './HealthcareBasicData';
import { PhoneNumberInput } from './PhoneNumberInput';
import { UsingParentContactDataCheckbox } from './UsingParentContactDataCheckbox';

type WithParentContactFormBean = {
   usingParentContactData: true;
   phoneNumber?: never;
   emailAddress?: never;
};

type WithOwnContactFormBean = {
   usingParentContactData: false;
   phoneNumber: PhoneNumber | null;
   emailAddress: EmailAddress | null;
};

export type HealthcareContactFormBean = WithParentContactFormBean | WithOwnContactFormBean;

export const createHealthcareContactFormBean = (
   usingParentContactData: boolean,
   healthcareBasicData: HealthcareBasicData
): HealthcareContactFormBean => {
   if (usingParentContactData) {
      return {
         usingParentContactData: true,
      };
   }

   return {
      usingParentContactData: false,
      phoneNumber: healthcareBasicData.phoneNumber ?? null,
      emailAddress: healthcareBasicData.emailAddress ?? null,
   };
};

const contactFormPartVisible = (
   register: BindingCreator<HealthcareContactFormBean>,
   usingParentContactData: boolean
): register is BindingCreator<HealthcareContactFormBean & { usingParentContactData: false }> => {
   return !usingParentContactData;
};

type HealthcareContactFormPartProps = Pick<FormContextValues<HealthcareContactFormBean>, 'register'> & {
   readonly usingParentContactData: boolean;
   readonly usingParentContactDataLabel: TranslatableText;
   readonly healthcareParentData: HealthcareBasicData;
};

export const HealthcareContactFormPart = (props: HealthcareContactFormPartProps): JSX.Element => {
   return (
      <AutoLayout as={'div'} $direction={'vertical'} $spacing={12}>
         <UsingParentContactDataCheckbox
            name={'usingParentContactData'}
            register={props.register}
            label={props.usingParentContactDataLabel}
         />
         {contactFormPartVisible(props.register, props.usingParentContactData) ? (
            <AutoLayout as={'div'} $direction={'horizontal'} $spacing={36}>
               <AutoLayout
                  as={PhoneNumberInput}
                  name={'phoneNumber'}
                  register={props.register}
                  required
                  $horizontalResizing={357}
               />
               <AutoLayout
                  as={EmailAddressInput}
                  name={'emailAddress'}
                  register={props.register}
                  $horizontalResizing={357}
               />
            </AutoLayout>
         ) : (
            <TextSeparator as={'div'} separator={TextSeparatorType.COMMA_SPACE}>
               {props.healthcareParentData.phoneNumber ? (
                  <Text testId={'inheritedPhoneNumber'}>
                     {phoneNumberFormatter(props.healthcareParentData.phoneNumber)}
                  </Text>
               ) : null}
               {props.healthcareParentData.emailAddress ? (
                  <Text testId={'inheritedEmailAddress'}>{props.healthcareParentData.emailAddress.asString()}</Text>
               ) : null}
            </TextSeparator>
         )}
      </AutoLayout>
   );
};
